/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy
} from '@angular/core';
import {StatefulComponent} from '../../../shared/stateful/stateful.component';
import {AlertFormState} from './models/alert-form.state';
import alertsConfigCreateNewConfiguration from './configuration/alerts-config-create-new.configuration';
import {AlertsConfigApi} from '../../../api/ms-settings/alerts-config/alerts-config.api';
import {SpinnerService} from '../../../shared/spinner/spinner.service';
import {Subscription} from 'rxjs';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ToastWrapperService} from '../../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {ConfirmModalComponent} from '../../../shared/confirm-modal/confirm-modal/confirm-modal.component';
import {EditAlertConfigModel} from '../alerts-main-page/models/edit-alert-config.model';
import {AlertEditableFormDataModel} from './alert-editable-form/models/alert-editable-form-data.model';
import {AlertConfigCommandContract} from '../../../api/ms-settings/alerts-config/contracts/alert-config-command.contract';
import {AlertsCreateNewMapper} from './mappers/alerts-create-new.mapper';
import {SpinnerContextTypeEnum} from '../../../shared/spinner/spinner-context-type.enum';
import {finalize} from 'rxjs/operators';
import {AlertConfigCommandResultBaseContract} from '../../../api/ms-settings/alerts-config/contracts/alert-config-command-result-base.contract';
import {UnsavedChangesService} from '../../../shared/guards/unsaved-work/unsaved-changes.service';
import {ReplayableRouterEventsService} from '../../../shared/services/replayable-router-events.service';

@Component({
    selector: 'app-alerts-form',
    templateUrl: './alerts-form.component.html',
    styleUrls: ['./alerts-form.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class AlertsFormComponent extends StatefulComponent<AlertFormState>
        implements OnInit, OnDestroy {
    @Input() editAlertConfig: EditAlertConfigModel;
    @Input() isViewMode: boolean;
    @Input() hasEditPermission: boolean;
    @Input() triggerLocalSpinner: EventEmitter<boolean>;
    @Output() closeAlertCreateNewForm = new EventEmitter<boolean>();
    @Output() lineToAnimated = new EventEmitter<string>();
    @Output() deleteAlert = new EventEmitter<string>();

    private subscriptions = new Subscription();
    private readonly LOCAL_LOADING_INSTANCE_KEY = 'ALERTS_FORM_LOADING';
    spinnerObservable = this.spinnerService.onLocalChange(this.LOCAL_LOADING_INSTANCE_KEY);

    constructor(private unsavedChangesService: UnsavedChangesService,
                private alertsConfigApi: AlertsConfigApi,
                private spinnerService: SpinnerService,
                private toast: ToastWrapperService,
                private modalService: NgbModal) {
        super();
    }

    ngOnInit(): void {
        const elementToScroll = document.getElementById(this.editAlertConfig?.code);
        elementToScroll?.scrollIntoView({block: 'start'});
        this.subscriptions.add(this.triggerLocalSpinner.subscribe((value: boolean) => value
                ? this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY)
                : this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY)));

        this.storeState({
            updatedBy: this.editAlertConfig?.editAlertConfigData?.updatedBy,
            updatedOn: this.editAlertConfig?.editAlertConfigData?.updatedOn,
            isValidToApply: false
        });
    }

    ngOnDestroy(): void {
        // reset originalValue for future from alterations
        this.unsavedChangesService.resetOriginalValue();
        this.subscriptions.unsubscribe();
    }

    onAlertEditableFormDataChange(data: AlertEditableFormDataModel): void {
        this.storeState({
            alertConfigData: data.alertConfigData,
            isAlertScheduleActive: data.isAlertScheduleActive,
            isValidToApply: data.isFormEligibleToApply
        });
    }

    discardAlertNewConfig(): void {
        if (this.unsavedChangesService.isChanged() && !this.isViewMode) {
            const discardModalConfiguration: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent).componentInstance;
            discardModalConfiguration.modalConfiguration = {
                ...alertsConfigCreateNewConfiguration.discardNewAlertsConfig
            };
            discardModalConfiguration.secondaryEvent.subscribe(() => this.closeAlertCreateNewFormAlertConfigForm());
        } else {
            this.closeAlertCreateNewFormAlertConfigForm();
        }
    }

    footerButtonPrimaryAction(): void {
        if (this.isViewMode) {
            this.isViewMode = false;
        } else {
            this.applyAlertNewConfig();
        }
    }

    footerButtonSecondaryAction(): void {
        if (this.isViewMode) {
            this.deleteAlert.emit(this.editAlertConfig.code);
        } else {
            this.discardAlertNewConfig();
        }
    }

    private applyAlertNewConfig(): void {
        const modalConfiguration: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent).componentInstance;
        if (this.editAlertConfig != null) {
            modalConfiguration.modalConfiguration = {
                ...alertsConfigCreateNewConfiguration.editAlertsConfig,
                body: {
                    title: `Do you want to update the ${this.editAlertConfig.editAlertConfigData.name} message?`
                }
            };
            modalConfiguration.primaryEvent.subscribe(() => this.subscribeEditAlertConfig());
        } else {
            modalConfiguration.modalConfiguration = {
                ...alertsConfigCreateNewConfiguration.applyNewAlertsConfig
            };
            modalConfiguration.primaryEvent.subscribe(() => this.subscribeNewAlertConfig());
        }
    }

    private closeAlertCreateNewFormAlertConfigForm(): void {
        this.closeAlertCreateNewForm.emit(false);
    };

    private subscribeNewAlertConfig(): void {
        const alertConfigCreateNew: AlertConfigCommandContract =
                AlertsCreateNewMapper.fromFormDataValueToAlertConfigCommandContract(
                        this.state.alertConfigData,
                        this.state.isAlertScheduleActive);
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
        this.subscriptions.add(
                this.alertsConfigApi.createAlertConfig(alertConfigCreateNew)
                        .pipe(finalize(() => {
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                        }))
                        .subscribe((alertConfig: AlertConfigCommandResultBaseContract) => {
                            const status = this.state.alertConfigData.active ? 'active' : 'inactive';
                            this.toast.success(`The Alert ${alertConfig.name}, was successfully created as ${status}.`,
                                    'Success');
                            this.closeAlertCreateNewFormAlertConfigForm();
                            this.lineToAnimated.emit(alertConfig.code);
                        })
        );
    }

    private subscribeEditAlertConfig(): void {
        const alertConfigEdit: AlertConfigCommandContract =
                AlertsCreateNewMapper.fromFormDataValueToAlertConfigCommandContract(
                        this.state.alertConfigData,
                        this.state.isAlertScheduleActive);
        this.spinnerService.start(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
        this.subscriptions.add(
                this.alertsConfigApi.editAlertConfig(this.editAlertConfig.code, alertConfigEdit)
                        .pipe(finalize(() => {
                            this.spinnerService.stop(SpinnerContextTypeEnum.LOCAL, this.LOCAL_LOADING_INSTANCE_KEY);
                        }))
                        .subscribe(() => {
                            const status = this.state.alertConfigData.active ? 'active' : 'inactive';
                            this.toast.success(`The Alert ${this.state.alertConfigData.name.value}, was successfully updated as ${status}.`
                                    , 'Success');
                            this.closeAlertCreateNewFormAlertConfigForm();
                            this.lineToAnimated.emit(this.editAlertConfig.code);
                        })
        );
    }
}
